import {ReactEditor, useSlate} from "slate-react"
import React, {Fragment, useRef} from "react"
import RichTextEditor from "../RichText/RichTextEditor"
import {Editor, Transforms} from "slate"
import {Box, Divider} from "@material-ui/core"
import ToolButton from "./ToolButton"
import ColorToolButton from "./ColorToolButton"
import EmojiToolButton from "./EmojiToolButton"
import SymbolToolButton from "./SymbolToolButton"
import ToggleToolButton from "./ToggleToolButton"
import ToolButtonGroup from "./ToolButtonGroup"
import SelectToolButton from "./SelectToolButton"
import dom from "../../core/dom"
import PropTypes from "prop-types";
import LinkToolButton from "./LinkToolButton";
import lang from "../../core/lang";
import { Icon } from '@iconify/react';
import roundTextIncrease from '@iconify/icons-ic/round-text-increase';
import roundTextDecrease from '@iconify/icons-ic/round-text-decrease';
import roundSuperscript from "@iconify/icons-ic/round-superscript";
import roundSubscript from "@iconify/icons-ic/round-subscript";
import formatBold from '@iconify/icons-ic/format-bold';
import formatItalic from '@iconify/icons-ic/format-italic';
import formatUnderlined from '@iconify/icons-ic/format-underlined';
import formatStrikethrough from '@iconify/icons-ic/format-strikethrough';
import code from '@iconify/icons-ic/code';
import formatColorFill from '@iconify/icons-ic/format-color-fill';
import formatColorText from '@iconify/icons-ic/format-color-text';
import formatAlignLeft from '@iconify/icons-ic/format-align-left';
import formatAlignCenter from '@iconify/icons-ic/format-align-center';
import formatAlignRight from '@iconify/icons-ic/format-align-right';
import formatAlignJustify from '@iconify/icons-ic/format-align-justify';
import formatListBulleted from '@iconify/icons-ic/format-list-bulleted';
import formatListNumbered from '@iconify/icons-ic/format-list-numbered';
import roundLink from '@iconify/icons-ic/round-link';
import roundLinkOff from '@iconify/icons-ic/round-link-off';
import image from '@iconify/icons-ic/image';
import sentimentSatisfied from '@iconify/icons-ic/sentiment-satisfied';
import roundEmojiSymbols from '@iconify/icons-ic/round-emoji-symbols';
import formatQuote from '@iconify/icons-ic/format-quote';
import roundDataArray from '@iconify/icons-ic/round-data-array';
import contentCopy from '@iconify/icons-ic/content-copy';
import clearIcon from '@iconify/icons-ic/clear-all';

function Toolbar(props) {
    const {className, mode, commands} = props
    const editor = useSlate()
    const ref = useRef()

    const handleTextFormat = ({value}) => {
        RichTextEditor.toggleMark(editor, value)
    }

    const handleFontSize = ({value}) => {
        let fontSize = RichTextEditor.getFontSize(editor) || dom.fontSize(ref?.current)
        if(value === 'increase') {
            fontSize++
        } else if (value === 'decrease') {
            fontSize--
        } else {
            fontSize = value
        }
        RichTextEditor.setFontSize(editor, fontSize)
        ReactEditor.focus(editor)
    }

    const handleSetTextBackground = ({value}) => {
        if(value === 'unset') {
            Editor.removeMark(editor, 'background')
        } else {
            Editor.addMark(editor, 'background', value)
        }
    }

    const handleSetTextColor = ({value}) => {
        if(value === 'unset') {
            Editor.removeMark(editor, 'color')
        } else {
            Editor.addMark(editor, 'color', value)
        }
    }

    const handleTextAlign = ({value}) => {
        RichTextEditor.toggleBlock(editor, value, 'align')
    }

    const handleBlock = ({value}) => {
        RichTextEditor.toggleBlock(editor, value)
    }

    const handleLink = ({value}) => {
        const {url, text} = value
        if(lang.isNullOrUndefined(url)) {
            RichTextEditor.removeLink(editor);
        } else {
            RichTextEditor.insertLink(editor, url, text, text);
        }
    }

    const handleUnlink = () => {
        RichTextEditor.removeLink(editor);
    }

    const handleInsertSymbol = ({value}) => {
        editor.insertText(value)
    }

    const handleInsertEmoji = ({value}) => {
        editor.insertText(value)
    }

    const handleCopy = async ({value}) => {
        await navigator.clipboard.writeText(RichTextEditor.serialize(editor, value))
    }

    const fontSizeOptions = [10, 10.5, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24]
    const size =  RichTextEditor.getFontSize(editor) || dom.fontSize(ref?.current)

    const isTextFormatChecked = value => {
        return RichTextEditor.isMarkActive(editor, value)
    }

    const isTextAlignChecked = value => {
        return RichTextEditor.isBlockActive(editor, value, 'align')
    }

    const isBlockSelected = value => {
        return RichTextEditor.isBlockActive(editor, value)
    }

    const handleClear = () => {
        const start = Editor.start(editor, [])
        const end = Editor.end(editor, [])
        const range = { anchor: start, focus: end }
        Transforms.delete(editor, {at: range})
    }

    const link = RichTextEditor.getSelectedLink(editor) || {};
    const text = RichTextEditor.getSelectedText(editor);
    if(!lang.isEmpty(text)) {
        link.text = text
    }

    return (
        <Box ref={ref} className={className} display='flex' flexDirection='row' width="100%" flexWrap={'wrap'}>
            <ToolButtonGroup onClick={handleTextFormat}>
                {commands.includes("bold") ? <ToggleToolButton value="bold" checked={isTextFormatChecked} icon={<Icon icon={formatBold} />} title="Bold" /> : null}
                {commands.includes("italic") ? <ToggleToolButton value="italic" checked={isTextFormatChecked}  icon={<Icon icon={formatItalic} />} title="Italic" /> : null}
                {commands.includes("underline") ? <ToggleToolButton value="underline" checked={isTextFormatChecked} icon={<Icon icon={formatUnderlined} />} title="Underline" /> : null}
                {commands.includes("strikethrough") ? <ToggleToolButton value="strikethrough" checked={isTextFormatChecked} icon={<Icon icon={formatStrikethrough} />} title="Strikethrough" /> : null}
                {commands.includes("superscript") ? <ToggleToolButton value="superscript" checked={isTextFormatChecked} icon={<Icon icon={roundSuperscript} />} title="Superscript" /> : null}
                {commands.includes("subscript") ? <ToggleToolButton value="subscript" checked={isTextFormatChecked} icon={<Icon icon={roundSubscript} />} title="Subscript" /> : null}
                {commands.includes("code") ? <ToggleToolButton value="code" checked={isTextFormatChecked} icon={<Icon icon={code} />} title="Code" /> : null}
            </ToolButtonGroup>
            <Divider orientation="vertical" flexItem />
            <ToolButtonGroup onClick={handleFontSize} >
                {commands.includes("size") ? <SelectToolButton value={size} options={fontSizeOptions} title="Font Size" /> : null}
                {commands.includes("increase") ? <ToolButton value="increase" icon={<Icon icon={roundTextIncrease} />} title="Increase Font Size" /> : null}
                {commands.includes("decrease") ? <ToolButton value="decrease" icon={<Icon icon={roundTextDecrease} />} title="Decrease Font Size" /> : null}
            </ToolButtonGroup>
            <Divider orientation="vertical" flexItem />
            <ToolButtonGroup>
                {commands.includes("background") ? <ColorToolButton value="background" icon={<Icon icon={formatColorFill} />} onClick={handleSetTextBackground} title="Text Highlight Color" /> : null}
                {commands.includes("color") ? <ColorToolButton value="color" icon={<Icon icon={formatColorText} />} onClick={handleSetTextColor} title="Text Color" /> : null}
            </ToolButtonGroup>
            <Divider orientation="vertical" flexItem />
            <ToolButtonGroup onClick={handleTextAlign}>
                {commands.includes("left") ? <ToggleToolButton value="left" checked={isTextAlignChecked} icon={<Icon icon={formatAlignLeft} />} title="Align Left" /> : null}
                {commands.includes("center") ? <ToggleToolButton value="center" checked={isTextAlignChecked} icon={<Icon icon={formatAlignCenter} />} title="Center Text" /> : null}
                {commands.includes("right") ? <ToggleToolButton value="right" checked={isTextAlignChecked} icon={<Icon icon={formatAlignRight} />} title="Align Right" /> : null}
                {commands.includes("justify") ? <ToggleToolButton value="justify" checked={isTextAlignChecked} icon={<Icon icon={formatAlignJustify} />} title="Justify" /> : null}
            </ToolButtonGroup>
            <Divider orientation="vertical" flexItem />
            <ToolButtonGroup onClick={handleBlock}>
                {commands.includes("bulleted") ? <ToggleToolButton value="bulleted-list" checked={isBlockSelected} icon={<Icon icon={formatListBulleted} />} title="Bullets" /> : null}
                {commands.includes("numbered") ? <ToggleToolButton value="numbered-list" checked={isBlockSelected} icon={<Icon icon={formatListNumbered} />} title="Numbering" /> : null}
            </ToolButtonGroup>
            <Divider orientation="vertical" flexItem />
            <ToolButtonGroup>
                {commands.includes("link") ? <LinkToolButton value={link} checked={link.url} onClick={handleLink} icon={<Icon icon={roundLink} />} title="Link" /> : null}
                {commands.includes("link") ? <ToggleToolButton value="unlink" checked={link.url} onClick={handleUnlink} icon={<Icon icon={roundLinkOff} />} title="Unlink" /> : null}
                {mode==='development' ? (
                    <Fragment>
                        {commands.includes("image") ? <ToggleToolButton value="image" icon={<Icon icon={image} />} title="Image" /> : null}
                    </Fragment>
                ): null}
                {commands.includes("symbol") ? <EmojiToolButton value="symbol" icon={<Icon icon={sentimentSatisfied} />} onClick={handleInsertEmoji} title="Emoji" /> : null}
                {commands.includes("emoji") ? <SymbolToolButton value="emoji" icon={<Icon icon={roundEmojiSymbols} />} onClick={handleInsertSymbol} title="Symbol" /> : null}
            </ToolButtonGroup>
            <Divider orientation="vertical" flexItem />
            <ToolButtonGroup onClick={handleBlock}>
                {commands.includes("quote") ? <ToggleToolButton value="quote" checked={isBlockSelected} icon={<Icon icon={formatQuote} />} title="Quote" /> : null}
                {commands.includes("code") ? <ToggleToolButton value="code" checked={isBlockSelected} icon={<Icon icon={roundDataArray} />} title="Code" /> : null}
            </ToolButtonGroup>
            {mode==='development' ? (
                <Fragment>
                    <Divider orientation="vertical" flexItem />
                    <ToolButtonGroup>
                        <ToolButton onClick={handleCopy} value="html" icon={<Icon icon={contentCopy} />} title="Copy" />
                        <ToolButton onClick={handleCopy} value="markdown" icon="M" title="Copy" />
                        <ToolButton onClick={handleCopy} value="json" icon="J" title="Copy" />
                        <ToolButton onClick={handleClear} value="clear" icon={<Icon icon={clearIcon} />} title="Clear" />
                    </ToolButtonGroup>
                </Fragment>
            ) : null}
        </Box>
    )
}

Toolbar.propTypes = {
    className: PropTypes.string,
    mode: PropTypes.string,
    commands: PropTypes.arrayOf(PropTypes.string)
}

Toolbar.defaultProps = {
    commands: ['bold', 'underline', 'italic', 'strikethrough', 'code', 'superscript', 'subscript',
    'left', 'center', 'right', 'justify',
    'size', 'increase', 'decrease',
    'color', 'background',
    'bulleted', 'numbered',
    'link', 'image',
    'symbol', 'emoji',
    'quote', 'code']
}

export default Toolbar